import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center  rounded-md text-sm font-medium transition-colors cursor-pointer disabled:pointer-events-none disabled:opacity-50 ",
  {
    variants: {
      variant: {
        cms_tabs: "bg-transparent text-gray-400 !rounded-none !px-2 !py-1",
        cms_tabs_active:
          "bg-transparent text-primary border-b border-primary !rounded-none !px-2 !py-1",
        hdStyling:
          "bg-white text-gray-950 hover:bg-gray-100 whitespace-nowrap border border-gray-300 rounded-m min-w-[199px] min-h-[24px] text-[14px] leading-[24px] font-raleway font-semibold tracking-normal p-10",
        default:
          "bg-primary text-white shadow hover:bg-primary-foreground whitespace-nowrap",
        destructive:
          "bg-red-600 text-white shadow-sm hover:bg-red-500 whitespace-nowrap",
        outline: "border border-input bg-white shadow-sm hover:bg-slate-100",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 whitespace-nowrap",
        ghost:
          "hover:bg-gray-200 hover:text-accent-foreground whitespace-nowrap",
        multiselect: "hover:border-primary hover:text-primary-foreground",
        link: "text-primary underline-offset-4 hover:underline whitespace-nowrap",
        not_selected:
          "bg-transparent text-[#667084] hover:bg-gray-100 whitespace-nowrap",
        white: "bg-white text-gray-950 hover:bg-gray-100 whitespace-nowrap",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        multiselect: "h-fit min-h-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
